import { Archive, Trash2 } from 'lucide-react';

/**
 * Shown IN PLACE of the editors when the product a user is editing stops being editable
 * under them — a teammate archived it, or deleted it — so the page says so, plainly, instead
 * of letting them keep typing into fields whose every save is about to be refused.
 *
 * The edit page notices on its own: its snapshot re-fetches on focus and every 15s (see
 * useProductForEdit), so switching back to a stale tab is enough. The server-side guard on
 * every write (409 `product_archived` / 404) is the backstop for whatever slips between polls.
 */
export function ProductUnavailableNotice({ kind }: { kind: 'archived' | 'deleted' }) {
  const archived = kind === 'archived';
  const Icon = archived ? Archive : Trash2;
  return (
    <section
      role="status"
      aria-live="polite"
      data-testid="product-unavailable"
      className="bg-white border border-line rounded-2xl p-6 shadow-card"
    >
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 size-5 shrink-0 text-muted" aria-hidden />
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-[15px] font-bold text-navy">
            {archived ? 'This product has been archived' : 'This product no longer exists'}
          </h2>
          <p className="mt-1 mb-0 text-[13.5px] leading-[1.6] text-muted">
            {archived ? (
              <>
                A teammate archived it while you had this page open, so it can no longer be
                edited. Nothing you were typing has been lost from the saved version — it is
                exactly as it was when it was archived. The page's own Back to products link above takes you to the list; it is under archived products there.
              </>
            ) : (
              <>
                A teammate deleted it while you had this page open. Anything not saved before
                that point was not kept.
              </>
            )}
          </p>
        </div>
      </div>
    </section>
  );
}
